import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import { invitesApi, type InvitePublicMeta } from '@/api/invites';
import { ApiError, errorMessage } from '@/api/client';
import { acceptInviteAndSync } from '@/features/auth/acceptInvite';
import { formatDateTime } from '@/lib/format';
import { useAuthStore } from '@/stores/auth';
import { readInviteToken } from '@/lib/inviteToken';

export default function AcceptInvitePage() {
  const { t } = useTranslation();
  const token = readInviteToken('token') ?? '';
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  const metaQuery = useQuery({
    queryKey: ['invite-meta', token],
    queryFn: () => invitesApi.getMeta(token),
    enabled: !!token,
    retry: false,
  });

  // Dacă user e deja logged in cu email-ul corect, oferă buton direct accept.
  const canAccept =
    !!user && metaQuery.data?.email.toLowerCase() === user.email.toLowerCase();
  const wrongEmail =
    !!user && metaQuery.data && !canAccept;

  const accept = async () => {
    setAcceptError(null);
    setAccepting(true);
    try {
      // Intrăm direct în spațiul nou — sesiunea rămâne aceeași, fără re-login.
      await acceptInviteAndSync(token);
      setAccepted(true);
      setTimeout(() => navigate('/vault', { replace: true }), 1200);
    } catch (err) {
      setAcceptError(errorMessage(err, t('common.error')));
    } finally {
      setAccepting(false);
    }
  };

  if (!token) {
    return (
      <Wrapper>
        <p className="text-sm text-red-600">{t('shell_ui.accept_invite.token_missing')}</p>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <h2 className="display-title-sm mb-2">{t('shell_ui.accept_invite.title')}</h2>

      {metaQuery.isLoading && <p className="text-sm text-[var(--text-dim)]">{t('shell_ui.accept_invite.checking')}</p>}
      {metaQuery.isError && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {metaQuery.error instanceof ApiError
            ? metaQuery.error.detail
            : t('shell_ui.accept_invite.invalid')}
        </div>
      )}

      {metaQuery.data && !accepted && (
        <InviteDetails
          meta={metaQuery.data}
          token={token}
          user={user}
          canAccept={canAccept}
          wrongEmail={!!wrongEmail}
          accept={accept}
          accepting={accepting}
          acceptError={acceptError}
        />
      )}

      {accepted && (
        <div className="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded px-3 py-2">
          {t('shell_ui.accept_invite.accepted')}
        </div>
      )}
    </Wrapper>
  );
}

function Wrapper({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div
      className="app-main-bg min-h-screen flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="display-title-lg">{t('app.name')}</h1>
        </div>
        <div className="card">{children}</div>
      </div>
    </div>
  );
}

function InviteDetails({
  meta,
  token,
  user,
  canAccept,
  wrongEmail,
  accept,
  accepting,
  acceptError,
}: {
  meta: InvitePublicMeta;
  token: string;
  user: { email: string } | null;
  canAccept: boolean;
  wrongEmail: boolean;
  accept: () => void;
  accepting: boolean;
  acceptError: string | null;
}) {
  const { t } = useTranslation();
  return (
    <>
      <dl className="text-sm space-y-2 mb-5 border-b border-[var(--border)] pb-4">
        <Row label={t('shell_ui.accept_invite.row_tenant')} value={meta.tenant_name} />
        <Row label={t('shell_ui.accept_invite.row_slug')} value={meta.tenant_slug} mono />
        <Row label={t('auth.email')} value={meta.email} mono />
        <Row label={t('tenant_invites.role_label')} value={meta.role} />
        <Row
          label={t('shell_ui.accept_invite.row_expires')}
          value={formatDateTime(meta.expires_at)}
        />
      </dl>

      {!user && (
        <div className="space-y-3">
          <p className="text-sm text-[var(--text-muted)]">
            <Trans
              i18nKey="shell_ui.accept_invite.login_prompt"
              values={{ email: meta.email }}
              components={{ mono: <span className="font-mono" /> }}
            />
          </p>
          <div className="flex gap-2">
            <Link
              to={`/login?email=${encodeURIComponent(meta.email)}#invite=${token}`}
              className="btn-primary flex-1 text-center"
            >
              {t('auth.login_title')}
            </Link>
            <Link
              to={`/register?email=${encodeURIComponent(meta.email)}#invite=${token}`}
              className="btn-outline flex-1 text-center"
            >
              {t('auth.register_title')}
            </Link>
          </div>
        </div>
      )}

      {wrongEmail && user && (
        <div className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded px-3 py-2">
          <Trans
            i18nKey="shell_ui.accept_invite.wrong_email"
            values={{ current: user.email, invited: meta.email }}
            components={{ mono: <span className="font-mono" /> }}
          />
        </div>
      )}

      {canAccept && (
        <>
          {acceptError && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2 mb-3">
              {acceptError}
            </div>
          )}
          <button
            type="button"
            className="btn-primary w-full"
            onClick={accept}
            disabled={accepting}
          >
            {accepting ? t('shell_ui.accept_invite.accepting') : t('shell_ui.accept_invite.accept')}
          </button>
        </>
      )}
    </>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex justify-between border-b border-[var(--border)] pb-1">
      <dt className="text-[var(--text-dim)] text-xs uppercase">{label}</dt>
      <dd className={mono ? 'font-mono text-sm' : 'text-sm'}>{value}</dd>
    </div>
  );
}
